import clsx from 'clsx';

import { Tooltip } from '../Tooltip/Tooltip';

import styles from './Stepper.module.scss';

interface StepperProps {
  steps: number;
  currentStep: number;
  maxStep: number;
  lastConfirmedStep?: number;
  onStepClick?: (step: number) => void | Promise<void>;
  /** Additive, opt-in restyle hook for the governance-evaluation questionnaire. Defaults to false (no visual change). */
  customQuestionnaire?: boolean;
}

function range(steps: number): number[] {
  return Array.from({ length: steps }, (x, index) => index);
}

export const Stepper = ({
  steps,
  currentStep,
  maxStep,
  lastConfirmedStep,
  onStepClick,
  customQuestionnaire = false,
}: StepperProps) => {
  if (currentStep > steps) throw new Error(`currentStep ${currentStep} is more than step count ${steps}`);

  // Use lastConfirmedStep if provided, otherwise fall back to maxStep
  const clickableStepLimit = lastConfirmedStep ?? maxStep;

  const handleStepClick = (step: number) => {
    if (onStepClick && step <= clickableStepLimit) {
      try {
        const result = onStepClick(step);
        if (result && typeof (result as Promise<void>).catch === 'function') {
          (result as Promise<void>).catch(() => undefined);
        }
      } catch {
        // ignore
      }
    }
  };

  return (
    <div className={clsx(styles.wrapper, customQuestionnaire && styles.customQuestionnaire)}>
      <p className={clsx(customQuestionnaire && styles.stepLabel)}>
        Step {currentStep} of {steps}
      </p>
      <div className={clsx(styles.steps, customQuestionnaire && styles.segmented)}>
        {range(steps).map(index => {
          const stepNumber = index + 1;
          const isClickable = stepNumber <= clickableStepLimit && !!onStepClick;
          const isUnconfirmed =
            lastConfirmedStep !== undefined && stepNumber > lastConfirmedStep && stepNumber <= maxStep;

          return isUnconfirmed ? (
            <Tooltip
              content={`This step needs review due to changes in earlier steps. Proceed to the next step with the "Next" button.`}
            >
              <div
                key={index}
                className={clsx(
                  styles.step,
                  maxStep >= stepNumber && styles.active,
                  currentStep === stepNumber && styles.current,
                  isClickable && styles.clickable,
                  isUnconfirmed && styles.unconfirmed,
                )}
                onClick={isClickable ? () => handleStepClick(stepNumber) : undefined}
              />
            </Tooltip>
          ) : (
            <div
              key={index}
              className={clsx(
                styles.step,
                maxStep >= stepNumber && styles.active,
                currentStep === stepNumber && styles.current,
                isClickable && styles.clickable,
                isUnconfirmed && styles.unconfirmed,
              )}
              onClick={isClickable ? () => handleStepClick(stepNumber) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};
